import axios from 'axios';
import {
  ASSIGN_USER_TO_PROJECT,
  CREATE_COMPANY,
  GET_COMPANIES,
  GET_COMPANY_TYPES,
  GET_USERS,
  ALL_USERS_URL,
  get_request_config, EDIT_PROJECT
} from '@/utils/system_constants/constant.js';
import alert_mixin from '@/utils/mixins/alert_mixin';
import {
  create_organization_project,
  fetch_organization_projects,
  fetch_organization_users,
  has_current_organization
} from '@/services/organization_service.js';

const parse_api_data = (response) => {
  const raw = response?.data ?? response;
  if (typeof raw === 'string') return JSON.parse(raw);
  return raw;
};

function unwrap_list(payload) {
  const parsed = parse_api_data(payload);
  const list = parsed?.data || parsed?.projects || parsed?.users || parsed || [];
  return Array.isArray(list) ? list : [];
}

const format_date_long = (val) => {
  if (!val) return '—';
  const date = new Date(val);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const DEFAULT_STATUS_PILL = {
  label: 'ACTIVE',
  cls: 'bg-primary-subtle text-primary'
};

const STATUS_PILL_RULES = [
  ['warn', { label: 'WARNING', cls: 'bg-warning-subtle text-warning' }],
  ['health', { label: 'HEALTHY', cls: 'bg-success-subtle text-success' }],
  ['active', DEFAULT_STATUS_PILL]
];

function get_default_status_label(status) {
  if (!status) return DEFAULT_STATUS_PILL.label;
  return String(status).toUpperCase();
}

const to_status_pill = (status) => {
  const s = String(status || '').toLowerCase();
  const matched_rule = STATUS_PILL_RULES.find(([keyword]) => s.includes(keyword));
  if (matched_rule) return matched_rule[1];

  return { ...DEFAULT_STATUS_PILL, label: get_default_status_label(status) };
};

const get_user_name = (user = {}) => {
  const full_name = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
  return full_name || user.email || 'Unnamed User';
};

const map_assigned_user = (profile = {}) => {
  const user = profile.user || profile;
  const role = user.role || {};

  return {
    id: profile.user_id || user.id,
    profile_id: profile.profile_id,
    name: get_user_name(user),
    email: user.email || '',
    role: role.role || role || 'User',
    is_active: profile.is_active ?? user.is_active
  };
};

const map_project = (project) => {
  const pill = to_status_pill(project?.status);

  return {
    id: project?.id,
    icon: 'bi-compass',
    icon_bg: 'entity-icon-surface',
    status: pill.label,
    status_class: pill.cls,
    title: project?.name || project?.project_name || 'Untitled Project',
    subtitle: project?.project_type?.name || project?.type?.name || '—',
    project_type_id:
      project?.project_type?.id ??
      project?.type?.id ??
      project?.project_type_id ??
      project?.type_id ??
      null,
    organization_id: project?.organization_id ?? project?.organization?.id ?? null,
    creator_id: project?.creator?.id ?? project?.creator_id ?? null,
    description: project?.project_description || project?.description || '',
    enable_compliance_task: Boolean(project?.enable_compliance_task),
    created: format_date_long(project?.date_created || project?.created_at),
    admin: project?.creator?.full_name || project?.admin?.full_name || '—',
    assigned_users_count: 0,
    assigned_users_loading: false
  };
};



export default {
  name: 'ProjectManagement',
  mixins: [alert_mixin],
  data() {
    return {
      metrics: { total_projects: 0, active_streams: 0, pending_syncs: 0 },
      is_loading: false,
      load_error: null,
      show_create_modal: false,
      create_error: null,
      project_name: '',
      project_description: '',
      selected_project_type: '',
      enable_compliance_task: false,
      project_types: [],
      project_types_loading: false,
      project_types_error: null,
      update_project_id: null,
      edit_project_name: '',
      edit_project_description: '',
      edit_selected_project_type: null,
      edit_enable_compliance_task: false,
      selected_project: null,
      assigned_users: [],
      assigned_users_loading: false,
      projects: [],
      users : [],
      users_loading: false,
      user_ids: [],
      user_search: '',
      user_toggle_loading: []
      
      
    };
  },
  computed: {
  is_organization_context() {
    return Boolean(this.$store.getters.organization) || has_current_organization();
  },
  can_manage_organization_projects() {
    return !this.is_organization_context
      || this.$store.getters.isOrganizationAdmin === true
      || this.$store.getters.isPlatformAdmin === true;
  },
  current_user_id() {
    return this.$store.getters.currentUserId || sessionStorage.getItem('current_user_id');
  },
  filtered_users() {
    const search = this.user_search.trim().toLowerCase();

    if (!search) {
      return this.users;
    }
    
    return this.users.filter(user => this.matchesUserSearch(user, search));
  }
},

  mounted() {
    if (!this.can_manage_organization_projects) {
      this.projects = [];
      this.load_error = 'You do not have permission to manage organization projects.';
      this.$router.replace({ name: 'GlobalOverview' });
      return;
    }
    this.get_project_types();
    this.fetch_projects();
  },

  methods: {
    validate_project_form() {
      const validations = [
        [!this.project_name || !this.project_name.trim(), 'Project name must not be empty'],
        [!this.project_description || !this.project_description.trim(), 'Project description must not be empty'],
        [!this.selected_project_type || this.selected_project_type < 1, 'Please select a valid project type']
      ];

      const failed_validation = validations.find(([condition]) => condition);
      if (!failed_validation) return true;

      this.show_error(failed_validation[1]);
      return false;
    },
    build_project_payload() {
      return {
        project_name: this.project_name,
        project_description: this.project_description,
        project_type_id: this.selected_project_type,
        enable_compliance_task: this.enable_compliance_task
      };
    },
    create_project_request(payload) {
      return this.is_organization_context
        ? create_organization_project(payload)
        : axios.post(CREATE_COMPANY, payload, get_request_config());
    },
    reset_project_form() {
      this.project_name = '';
      this.project_description = '';
      this.selected_project_type = '';
      this.enable_compliance_task = false;
    },
    view_project(project) {
      if (!project?.id) return;

      this.$router.push({
        name: 'EntityConfigs',
        query: {
          project_id: String(project.id),
          project_name: project.title || project.name || ''
        }
      });
    },
     matchesUserSearch(user, search) {
    const fields = [
      user.name,
      user.email,
      user.role
    ];

    return fields.some(field =>
      (field || "").toLowerCase().includes(search)
    );
  },
   async open_users_modal(project) {
  if (!project?.id) return;

  this.selected_project = project;
  this.user_ids = [];
  this.users_loading = true;

  try {
    const rawUsers = has_current_organization()
      ? unwrap_list(await fetch_organization_users())
      : unwrap_list(await axios.get(ALL_USERS_URL, get_request_config()));

    const assignedUsers = await this.fetch_assigned_users(project.id);
    const assignedIds = assignedUsers.map(
      user_entity => user_entity.user_id || user_entity.id
    );
    this.assigned_users = await this.fetch_assigned_users(project.id);
    project.assigned_users_count = this.assigned_users.length;

    this.users = rawUsers.map(profile => {
      const user_entity = profile.user || profile || {};
      return {
        id: profile.user_id || user_entity.id,
        name: [user_entity.first_name, user_entity.last_name].filter(Boolean).join(" ").trim() || "Unnamed User",
        email: user_entity.email || "",
        is_assigned: assignedIds.includes(profile.user_id || user_entity.id)
      };
      
    });
  } catch (error) {
    this.show_error("Failed to load users.");
  } finally {
    this.users_loading = false;
  }
},


is_user_selected(user_id) {
      return (Array.isArray(this.user_search.user_ids) ? this.user_search.user_ids : [])
        .map(String)
        .includes(String(user_id));
    },
    get_project_types() {
      return axios.get(GET_COMPANY_TYPES, get_request_config())
        .then(response => {
          const parsed_response = parse_api_data(response);
          if (parsed_response.data && parsed_response.data.length > 0) {
            this.project_types = parsed_response.data;
          } else {
            this.show_error('No project types found');
          }
        })
        .catch(error => {
          this.show_error('Error getting project types: ' + error);
        });
    },

    async create_project() {
      if (!this.validate_project_form()) return;

      const payload = this.build_project_payload();
      const loader = await this.show_loader('Creating project...');

      try {
        await this.create_project_request(payload);
        await this.fetch_projects();
        this.reset_project_form();
        this.close_modal('create_project');
      } catch (error) {
        this.show_error('Error creating project: ' + error);
      } finally {
        await this.close_loader(loader);
      }
    },

    async fetch_projects() {
      this.is_loading = true;
      this.load_error = null;

      try {
        if (!this.can_manage_organization_projects) {
          this.projects = [];
          this.load_error = 'You do not have permission to manage organization projects.';
          return;
        }

        const projectPayload = this.is_organization_context
          ? await fetch_organization_projects()
          : await axios.get(GET_COMPANIES, get_request_config());
        const parsed = parse_api_data(projectPayload);
        const projects = unwrap_list(projectPayload);

        const mapped_projects = projects.map(map_project).filter(p => p.id != null);
        this.projects = this.is_organization_context || this.$store.getters.isPlatformAdmin === true
          ? mapped_projects
          : mapped_projects.filter(project =>
            String(project.creator_id || '') === String(this.current_user_id || '')
          );
        this.metrics.total_projects = this.projects.length;
        await this.load_project_assigned_user_counts();

        if (parsed?.metrics) {
          const { active_streams, pending_syncs } = parsed.metrics;

          this.metrics.active_streams =
            active_streams ?? this.metrics.active_streams;

          this.metrics.pending_syncs =
          pending_syncs ?? this.metrics.pending_syncs;
}
        const highlight_id = this.$route?.query?.highlight;
        if (highlight_id != null) {
          this.$nextTick(() => {
            const el = document.getElementById(`project-${highlight_id}`);
            if (el?.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          });
        }
      } catch (err) {
        const detail = err?.response?.data?.detail;
        const message = err?.response?.data?.message;
        this.load_error = message || detail || 'Failed to load projects.';
      } finally {
        this.is_loading = false;
      }
    },

    async fetch_assigned_users(project_id) {
      const response = await axios.get(
        GET_USERS,
        get_request_config({
          params: {
            project_id: project_id,
            page: 1,
            page_size: 100
          }
        })
      );
      const response_data = parse_api_data(response);
      const items = Array.isArray(response_data.data) ? response_data.data : [];

      return items.map(map_assigned_user).filter((user) => user.id != null);
    },

    async load_project_assigned_user_counts() {
      const results = await Promise.allSettled(
        this.projects.map(async (project) => {
          project.assigned_users_loading = true;
          const users = await this.fetch_assigned_users(project.id);
          project.assigned_users_count = users.length;
          project.assigned_users_loading = false;
        })
      );

      results.forEach((result, index) => {
        if (result.status === 'rejected' && this.projects[index]) {
          this.projects[index].assigned_users_loading = false;
          this.projects[index].assigned_users_count = 0;
        }
      });
    },
    user_display_name(user) {
      return user?.user_name || user?.name || `User ${user?.id || ''}`;
    },
    assigned_users_label(project) {
      if (project.assigned_users_loading) return 'Loading assigned users...';

      const count = Number(project.assigned_users_count || 0);
      return `${count} Assigned User(s)`;
    },

    async open_assigned_users_modal(project) {
      if (!project?.id) return;

      this.selected_project = project;
      this.assigned_users = [];
      this.assigned_users_loading = true;

      try {
        this.assigned_users = await this.fetch_assigned_users(project.id);
        project.assigned_users_count = this.assigned_users.length;
      } catch (error) {
        this.show_error('Failed to load assigned users.');
      } finally {
        this.assigned_users_loading = false;
      }
    },

    unassign_user_from_project(user) {
      if (!this.selected_project?.id || !user?.id) return;

      this.show_confirm(
        'Unassign User',
        `Are you sure you want to unassign ${user.name} from ${this.selected_project.title}?`,
        async () => {
          const assigner_id = this.$store.getters.currentUserId;
          if (!assigner_id) {
            this.show_error('Current user could not be resolved.');
            return;
          }

          const loader = await this.show_loader('Unassigning user...');

          try {
            const response = await axios.post(
              ASSIGN_USER_TO_PROJECT,
              {
                project: Number(this.selected_project.id),
                assigner: Number(assigner_id),
                assignee: Number(user.id)
              },
              get_request_config()
            );
            const response_data = parse_api_data(response);

            if (response_data?.status && response_data.status !== 'success') {
              this.show_error(response_data.message || 'Unassign failed.');
              return;
            }
            this.assigned_users = this.assigned_users.filter(
              (assigned_user) => String(assigned_user.id) !== String(user.id)
 
            );
            this.selected_project.assigned_users_count = this.assigned_users.length;
            this.show_success(response_data.message || 'User unassigned successfully.');
          } catch (error) {
            const message = error?.response?.data?.message || error?.message || 'Failed to unassign user.';
            this.show_error(message);
          } finally {
            this.close_loader(loader);
          }
        }
      );
    },
   async assign_user_to_project(user) {
  const error = this.validate_assignment(user);
  if (error) {
    this.show_error(error);
    return;
  }

  const loader = await this.show_loader("Assigning user...");

  try {
    const response_data = await this.assign_project_user_request(user);
    this.process_assignment_response(user, response_data);
  } catch (error) {
    this.show_error(this.get_assignment_error(error));
  } finally {
    this.close_loader(loader);
  }
},
process_assignment_response(user, response_data) {
  if (this.assignment_failed(response_data)) {
    this.show_error(response_data?.message || "Assignment failed.");
    return;
  }

  this.handle_assignment_success(user, response_data);
},
assignment_failed(response_data) {
  return response_data?.status && response_data.status !== "success";
},
  validate_assignment(user) {
    if (!this.selected_project?.id || !user?.id) {
      return "Project or user not found.";
    }

    if (!this.$store.getters.currentUserId) {
      return "Current user could not be resolved.";
    }

    return null;
  },

  async assign_project_user_request(user) {
    const response = await axios.post(
      ASSIGN_USER_TO_PROJECT,
      {
        project: Number(this.selected_project.id),
        assigner: Number(this.$store.getters.currentUserId),
        assignee: Number(user.id)
      },
      get_request_config()
    );

    return parse_api_data(response);
  },

  handle_assignment_success(user, response_data) {
    this.assigned_users.push(user);
    this.selected_project.assigned_users_count = this.assigned_users.length;

    this.show_success(
      response_data.message || "User assigned successfully."
    );
  },

  get_assignment_error(error) {
    return (
      error?.response?.data?.message ||
      error?.message ||
      "Failed to assign user."
    );
  },
async toggle_user_assignment(user) {
      
  if (user.is_assigned) {
    await this.unassign_user_from_project(user);
  } else {
    await this.assign_user_to_project(user);
  }

  user.is_assigned = !user.is_assigned;
  
},
 user_selected(userId) {
    return this.assigned_users.some(
      assignedUser => Number(assignedUser.id) === Number(userId)
    );
  },

  is_user_toggle_loading(userId) {
    return this.user_toggle_loading.includes(Number(userId));
  },

  async on_user_toggle(user, event) {

    const checked = event.target.checked;

    this.user_toggle_loading.push(Number(user.id));

    try {

      if (checked) {
        await this.assign_user_to_project(user);
      } else {
        await this.unassign_user_from_project(user);
      }

    } finally {

      this.user_toggle_loading =
        this.user_toggle_loading.filter(
          id => id !== Number(user.id)
        );

    }

  },


    open_edit_project_modal(project){
      if (!project) return;

      this.update_project_id = project.id;
      this.edit_project_name = project.title;
      this.edit_project_description = project.description;
      this.edit_selected_project_type = project.project_type_id || null;
      this.edit_enable_compliance_task = Boolean(project.enable_compliance_task);
    },

    async update_project(){
      if (!this.edit_project_name || !this.edit_project_name.trim()) {
        this.show_error('Project name must not be empty');
        return;
      }

      if (!this.edit_project_description || !this.edit_project_description.trim()) {
        this.show_error('Project description must not be empty');
        return;
      }

      if (!this.edit_selected_project_type) {
        this.show_error('Please select a valid project type');
        return;
      }

      const payload = {
        project_id: this.update_project_id,
        project_name: this.edit_project_name,
        project_description: this.edit_project_description,
        project_type_id: Number(this.edit_selected_project_type),
        enable_compliance_task: this.edit_enable_compliance_task,
      };

      const loader = await this.show_loader('Updating project...');

      try {
        await axios.post(EDIT_PROJECT, payload, get_request_config());

        this.show_success("Project updated successfully");
        this.close_modal("edit_project");

        await this.fetch_projects();

      } catch (error) {
        this.show_error("Error updating project: " + error);
      } finally {
        this.close_loader(loader);
      }
    },

  }
};
